<script setup lang="ts">
/**
 * The call, on screen.
 *
 * One component for both ends and every stage, because they are the same
 * picture — a face, a name, a line of status and a row of buttons — and only
 * the buttons differ. Three components would have meant three places to keep
 * the face in the same spot.
 *
 * **Teleported and fixed.** A call outlives the page it started on: it begins
 * in a chat or the inbox and must survive walking to the other person's
 * profile. So this is mounted once, at the app shell, and reads the store
 * rather than taking props.
 *
 * It is deliberately loud. A ring that can be mistaken for a notification gets
 * missed, and a call that is still live while the user reads a feed is how
 * somebody talks into a muted microphone for a minute.
 */
import { computed } from 'vue';

import Avatar from './Avatar.vue';
import NavIcon from './NavIcon.vue';
import { useCallStore } from '../stores/call.ts';
import { useI18n } from '../i18n/index.ts';

const { t } = useI18n();
const call = useCallStore();

/** `7:04`, counted from the moment the audio actually connected. */
const elapsed = computed(() => {
  const total = call.seconds;
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${String(secs).padStart(2, '0')}`;
});

/**
 * The one line that says what is happening.
 *
 * Every phase has one, including the ones that are over: a call that simply
 * vanished leaves the other person wondering whether they were hung up on, and
 * "Declined" answers that in a word.
 */
const status = computed(() => {
  switch (call.phase) {
    case 'ringing-out':
      return t('calls.ringing');
    case 'ringing-in':
      return t('calls.incoming');
    case 'connecting':
      return t('calls.connecting');
    case 'active':
      return elapsed.value;
    case 'failed':
      // The microphone being refused is the common failure and has nothing to
      // do with the network, so it is said as itself.
      return t(call.errorKey ?? 'calls.failed');
    case 'ended':
      switch (call.endReason) {
        case 'declined':
          return t('calls.declined');
        case 'busy':
          return t('calls.busy');
        case 'unanswered':
          return t('calls.unanswered');
        case 'failed':
          return t('calls.failed');
        default:
          return t('calls.ended');
      }
    default:
      return '';
  }
});

const over = computed(() => call.phase === 'ended' || call.phase === 'failed');
</script>

<template>
  <Teleport to="body">
    <div
      v-if="call.inCall"
      class="fixed inset-x-0 bottom-0 z-[60] sm:inset-auto sm:bottom-6 sm:right-6"
      role="dialog"
      aria-modal="false"
      :aria-label="t('calls.title')"
    >
      <div
        class="tv-glass w-full rounded-t-2xl border-t border-border-subtle p-5 shadow-xl sm:w-80 sm:rounded-2xl sm:border"
      >
        <div class="flex flex-col items-center text-center">
          <!--
            An incoming call gets a ring round the face. A *static* ring: it was
            `animate-pulse` first, and nothing in this app animates — a test
            reads every file under src/ for exactly that, and caught it.
          -->
          <Avatar
            :name="call.peer?.displayName ?? ''"
            :src="call.peer?.avatarUrl ?? null"
            :user-id="call.peer?.id"
            size="xl"
            :class="call.phase === 'ringing-in' ? 'ring-4 ring-emerald-500/60' : ''"
          />
          <p class="mt-3 text-base font-semibold">{{ call.peer?.displayName }}</p>
          <p
            class="mt-0.5 text-sm text-ink-muted"
            role="status"
            aria-live="polite"
            :class="call.phase === 'active' ? 'font-mono tabular-nums' : ''"
          >
            {{ status }}
          </p>

          <!--
            Said only when it is the likely cause, and only after a failure.
            A relay is what carries a call between two networks that will not
            talk to each other directly, and without one those calls cannot
            connect at all — so when there is none, the honest thing is to name
            it rather than let somebody blame their wifi.
          -->
          <p v-if="call.relayMissing && over" class="mt-2 text-xs text-ink-faint">
            {{ t('calls.noRelay') }}
          </p>
        </div>

        <!-- Incoming: answer or refuse, and nothing else. -->
        <div v-if="call.phase === 'ringing-in'" class="mt-5 flex items-center justify-center gap-3">
          <button
            type="button"
            class="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-500 text-white transition-opacity hover:opacity-90"
            :aria-label="t('calls.decline')"
            :title="t('calls.decline')"
            @click="call.decline()"
          >
            <NavIcon name="phone-off" class="h-5 w-5" filled />
          </button>
          <button
            type="button"
            class="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white transition-opacity hover:opacity-90"
            :aria-label="t('calls.accept')"
            :title="t('calls.accept')"
            @click="void call.accept()"
          >
            <NavIcon name="phone" class="h-5 w-5" filled />
          </button>
        </div>

        <!-- In progress, from either end: mute and hang up. -->
        <div v-else-if="!over" class="mt-5 flex items-center justify-center gap-3">
          <button
            type="button"
            class="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border-subtle transition-colors"
            :class="call.muted ? 'bg-surface-sunken text-ink-faint' : 'bg-surface text-ink'"
            :aria-pressed="call.muted"
            :aria-label="call.muted ? t('calls.unmute') : t('calls.mute')"
            :title="call.muted ? t('calls.unmute') : t('calls.mute')"
            @click="call.toggleMute()"
          >
            <NavIcon name="mic" class="h-5 w-5" :filled="!call.muted" />
          </button>
          <button
            type="button"
            class="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-500 text-white transition-opacity hover:opacity-90"
            :aria-label="t('calls.hangUp')"
            :title="t('calls.hangUp')"
            @click="call.hangUp()"
          >
            <NavIcon name="phone-off" class="h-5 w-5" filled />
          </button>
        </div>

        <!--
          Over, and still on screen until it is dismissed. It does not close
          itself on a timer: the reason is the only record of what happened, and
          a panel that disappears while being read is worse than one more click.
        -->
        <div v-else class="mt-5 flex justify-center">
          <button
            type="button"
            class="rounded-xl bg-surface-sunken px-4 py-2 text-sm font-medium transition-colors hover:bg-surface"
            @click="call.dismiss()"
          >
            {{ t('common.close') }}
          </button>
        </div>

        <!-- Muted, said in words as well as in the button's state. -->
        <p v-if="call.muted && call.phase === 'active'" class="mt-3 text-center text-xs text-ink-faint">
          {{ t('calls.mutedNotice') }}
        </p>
      </div>
    </div>
  </Teleport>
</template>
